<template>
  <v-app class="resident-app">
    <v-navigation-drawer
      v-model="drawer"
      app
      temporary
      width="286"
      class="resident-drawer"
    >
      <div class="drawer-content">
        <router-link class="brand" to="/home">
          <span class="brand-mark"><img src="/img/boarua-icon-concept-v1.png" alt="" /></span>
          <span>
            <strong>BOARUA</strong>
            <small>{{ associationName }}</small>
          </span>
        </router-link>

        <nav class="drawer-nav">
          <div v-for="menuGroup in sidebarMenu" :key="menuGroup.name" class="menu-group">
            <p>{{ menuGroup.name }}</p>
            <button
              v-for="menuOption in menuGroup.children"
              :key="menuOption.route"
              :class="['nav-item', { active: isActive(menuOption.route) }]"
              type="button"
              @click="goTo(menuOption)"
            >
              <v-icon size="20">{{ menuOption.icon }}</v-icon>
              <span>{{ menuOption.name }}</span>
            </button>
          </div>
        </nav>
      </div>
    </v-navigation-drawer>

    <header class="resident-topbar">
      <v-btn icon class="menu-button" @click="drawer = true">
        <v-icon>mdi-menu</v-icon>
      </v-btn>
      <router-link class="brand compact" to="/home">
        <span class="brand-mark"><img src="/img/boarua-icon-concept-v1.png" alt="" /></span>
        <span>
          <strong>BOARUA</strong>
          <small>{{ associationName }}</small>
        </span>
      </router-link>
      <v-menu min-width="200" offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-avatar size="36" v-bind="attrs" v-on="on">
            <img :src="avatarUrl" />
          </v-avatar>
        </template>
        <v-list dense>
          <MenuItem
            v-for="(menuOption, index) in profileMenu"
            :key="index"
            :item="menuOption"
            v-if="!menuOption || !menuOption.children"
          />
        </v-list>
      </v-menu>
    </header>

    <main class="resident-main">
      <router-view />
      <PoweredByFooter bottom-nav />
    </main>

    <nav v-if="showBottomNav" class="bottom-nav" aria-label="Navegação principal">
      <button
        v-for="item in bottomNav"
        :key="item.route"
        :class="['bottom-nav-item', { active: isActive(item.route) }]"
        type="button"
        @click="goTo(item)"
      >
        <v-icon size="22">{{ item.icon }}</v-icon>
        <span>{{ item.name }}</span>
      </button>
    </nav>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout" bottom>
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Fechar</v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import router from "../router";
import { eventHub } from "@/main";
import MenuItem from "@/layouts/components/MenuItem";
import PoweredByFooter from "@/components/PoweredByFooter.vue";
import profileMenu from "@/common/data/profile-menu.data";
import sidebarMenu from "@/common/data/sidebar-menu.data";
import { registerBoaruaPush, unregisterBoaruaPush } from "@/plugins/push-notifications";

export default {
  name: "PrivateLayout",
  components: { MenuItem, PoweredByFooter },
  data() {
    return {
      drawer: false,
      user: null,
      sidebarMenu,
      profileMenu,
      snackbar: {
        show: false,
        message: "",
        color: "info",
        timeout: 5000,
      },
      bottomNav: [
        { name: "Início", icon: "mdi-home-variant-outline", route: "/home" },
        { name: "Segurança", icon: "mdi-shield-alert-outline", route: "/seguranca" },
        { name: "Encomendas", icon: "mdi-package-variant-closed", route: "/encomendas" },
        { name: "Chamados", icon: "mdi-clipboard-text-outline", route: "/demandas" },
        { name: "Ofertas", icon: "mdi-storefront-outline", route: "/comercio-local" },
      ],
    };
  },
  computed: {
    associationName() {
      const profile = JSON.parse(localStorage.getItem("associationProfile") || "{}");
      return profile.association ? profile.association.name : "Meu local";
    },
    avatarUrl() {
      const name = this.user ? this.user.name : "Morador BOARUA";
      return `https://ui-avatars.com/api/?background=057a3d&color=fff&name=${encodeURIComponent(name)}`;
    },
    isDesktop() {
      return this.$vuetify.breakpoint.mdAndUp;
    },
    showBottomNav() {
      return !["/perfis"].includes(this.$route.path);
    },
  },
  watch: {
    isDesktop(value) {
      if (!value) {
        this.drawer = false;
      }
    },
  },
  created() {
    this.user = this.$root.user.data || this.$root.user;
    eventHub.$on("logout", () => this.logout());
    eventHub.$on("disable-notifications", () => this.disableNotifications());
    window.addEventListener("boarua-api-error", this.handleApiError);
    this.registerNotifications();
  },
  destroyed() {
    eventHub.$off("logout");
    eventHub.$off("disable-notifications");
    window.removeEventListener("boarua-api-error", this.handleApiError);
  },
  methods: {
    handleApiError(event) {
      const detail = event && event.detail ? event.detail : {};
      this.snackbar = {
        show: true,
        message: detail.message || "Não foi possível carregar os dados agora.",
        color: detail.code === "ASSOCIATION_ACCESS_BLOCKED" ? "warning" : "error",
        timeout: detail.code === "ASSOCIATION_ACCESS_BLOCKED" ? 8000 : 5000,
      };
    },
    isActive(route) {
      return this.$route.path === route || this.$route.path.indexOf(`${route}/`) === 0;
    },
    goTo(item) {
      if (item.event === "logout") {
        this.logout();
        return;
      }
      if (item.route && this.$route.path !== item.route) {
        this.$router.push(item.route);
      }
      this.drawer = false;
    },
    async logout() {
      localStorage.clear();
      router.push("/login");
    },
    async registerNotifications() {
      try {
        await registerBoaruaPush();
      } catch (error) {
        console.error("[BOARUA] Falha ao registrar notificacoes", error);
      }
    },
    async disableNotifications() {
      try {
        await unregisterBoaruaPush();
        this.snackbar = {
          show: true,
          message: "Notificacoes canceladas neste dispositivo.",
          color: "primary",
          timeout: 5000,
        };
      } catch (error) {
        console.error("[BOARUA] Falha ao cancelar notificacoes", error);
        this.snackbar = {
          show: true,
          message: "Nao foi possivel cancelar as notificacoes agora.",
          color: "error",
          timeout: 5000,
        };
      }
    },
  },
};
</script>

<style scoped lang="scss">
.resident-app {
  background: #f6f8f6;
  overflow-x: hidden;
}

.resident-topbar {
  height: 66px;
  padding: 10px 16px;
  position: sticky;
  top: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #ffffff;
  border-bottom: 1px solid #edf0ed;
}

.menu-button {
  background: #f5f7f5;
  border: 1px solid #dfe7df;
  border-radius: 8px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 11px;
  color: #111827;
  text-decoration: none;

  strong,
  small {
    display: block;
    line-height: 1.15;
  }

  strong {
    font-size: 16px;
  }

  small {
    max-width: 160px;
    overflow: hidden;
    color: #66736b;
    font-size: 11px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &.compact {
    flex: 1;
    min-width: 0;
  }
}

.brand-mark {
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #ffffff;
  border-radius: 8px;

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }
}

.resident-main {
  min-height: calc(100vh - 72px);
  padding: 0;
  background: #f6f8f6;
  overflow-x: hidden;
}

.bottom-nav {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 8;
  height: calc(72px + env(safe-area-inset-bottom));
  padding: 8px 10px calc(8px + env(safe-area-inset-bottom));
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 4px;
  background: rgba(255, 255, 255, 0.96);
  border-top: 1px solid #e5ebe6;
  box-shadow: 0 -12px 30px rgba(17, 24, 39, 0.06);
  backdrop-filter: blur(12px);
}

.bottom-nav-item {
  min-width: 0;
  padding: 5px 2px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  color: #68736d;
  background: transparent;
  border: 0;
  border-radius: 14px;
  font-size: 10px;
  font-weight: 750;
  letter-spacing: 0;

  .v-icon {
    color: #68736d;
  }

  &.active {
    color: #057a3d;
    background: #eef8f1;

    .v-icon {
      color: #057a3d;
    }
  }
}

@media (min-width: 960px) {
  .resident-main {
    padding-bottom: 0;
  }

  .bottom-nav {
    right: 50%;
    left: 50%;
    width: min(100%, 560px);
    transform: translateX(-50%);
    border: 1px solid #e5ebe6;
    border-bottom: 0;
    border-radius: 22px 22px 0 0;
  }
}

.resident-drawer {
  background: #ffffff;
  border-right: 1px solid #dfe7df;
}

.drawer-content {
  padding: 22px 16px;
}

.drawer-nav {
  margin-top: 26px;
}

.menu-group {
  margin-bottom: 22px;

  p {
    margin: 0 0 8px;
    padding: 0 10px;
    color: #8b948d;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
  }
}

.nav-item {
  width: 100%;
  min-height: 42px;
  margin-bottom: 5px;
  padding: 0 11px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #303832;
  background: transparent;
  border: 0;
  border-radius: 8px;
  font-size: 14px;
  text-align: left;

  .v-icon {
    color: #5f6a62;
  }

  &.active,
  &:hover {
    color: #ffffff;
    background: linear-gradient(135deg, #111827, #015a31);

    .v-icon {
      color: #ffffff;
    }
  }
}
</style>
